import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Eye, Trash2, Plus, X, Users, Mail, Loader2 } from 'lucide-react';
import platformApi from '../api/platform';

const PlatformOrganizations = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', admin_email: '', admin_name: '' });
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [createResult, setCreateResult] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await platformApi.listTenants();
      setTenants(data.tenants || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateResult(null);
    try {
      const result = await platformApi.createTenant(createForm);
      setCreateResult(result);
      setCreateForm({ name: '', admin_email: '', admin_name: '' });
      await fetchTenants();
    } catch (err) {
      setCreateResult({ error: err.response?.data?.error || 'Failed to create organization' });
    } finally {
      setCreating(false);
    }
  };

  const handleViewMembers = async (tenantId) => {
    setSelectedTenant(tenantId);
    setMembersLoading(true);
    try {
      const data = await platformApi.listTenantMembers(tenantId);
      setMembers(data.members || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load members');
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) return;
    setDeletingId(tenantId);
    try {
      await platformApi.deleteTenant(tenantId);
      setTenants(prev => prev.filter(t => t.id !== tenantId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete organization');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = tenants.filter(t =>
    (t.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // ─── Members View ────────────────────────────────────────────
  if (selectedTenant) {
    const tenant = tenants.find(t => t.id === selectedTenant);
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setSelectedTenant(null); setMembers([]); }}
            className="inline-flex items-center px-3 py-1.5 bg-dark-800 border border-gray-700 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{tenant?.name || 'Organization'} Members</h1>
            <p className="text-sm text-gray-500 mt-1">
              <Users className="w-3.5 h-3.5 inline mr-1" />
              {members.length} member{members.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="bg-dark-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {membersLoading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-12 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 inline animate-spin mr-2" />
                    Loading members...
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-16 text-center">
                    <Users className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400">No members found</p>
                    <p className="text-gray-600 text-sm mt-1">Invite users to this organization to get started</p>
                  </td>
                </tr>
              ) : (
                members.map((member, idx) => (
                  <tr key={member.email || member.id || idx} className="border-b border-gray-700/50 hover:bg-dark-800 transition-colors">
                    <td className="px-4 py-3 text-sm text-white">
                      <Mail className="w-3.5 h-3.5 inline mr-2 text-gray-500" />
                      {member.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 capitalize">{member.role || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member.status !== 'inactive'
                          ? 'bg-green-400/10 text-green-400'
                          : 'bg-gray-400/10 text-gray-400'
                      }`}>
                        {member.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {error && <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-2">{error}</div>}
      </div>
    );
  }

  // ─── Main List View ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-gray-400">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error && tenants.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-red-500/50 mx-auto mb-4" />
          <p className="text-red-400 text-lg mb-2">Failed to load organizations</p>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all organizations on the platform</p>
        </div>
        <button
          onClick={() => { setShowCreateForm(true); setCreateResult(null); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-orange-500/20"
        >
          <Plus className="w-4 h-4" />
          Create Organization
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search by organization name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {/* Create Organization Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 w-full max-w-md border border-gray-700 shadow-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white">Create Organization</h2>
              <button
                onClick={() => { setShowCreateForm(false); setCreateResult(null); }}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-gray-500 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {createResult?.invite_link ? (
              /* Success state — show invite link */
              <div className="space-y-4">
                <div className="bg-green-400/10 border border-green-400/20 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-green-400 font-medium">Organization created successfully</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Admin Invite Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={createResult.invite_link}
                      className="flex-1 px-3 py-2 bg-dark-900 border border-gray-700 rounded-lg text-sm text-gray-300 font-mono focus:outline-none"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(createResult.invite_link); }}
                      className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => { setShowCreateForm(false); setCreateResult(null); }}
                  className="w-full py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 rounded-lg text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              /* Form state */
              <form onSubmit={handleCreateOrg} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Organization Name *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Acme Corp"
                    required
                    className="w-full px-4 py-2.5 bg-dark-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Admin Email *</label>
                  <input
                    type="email"
                    value={createForm.admin_email}
                    onChange={e => setCreateForm(prev => ({ ...prev, admin_email: e.target.value }))}
                    placeholder="admin@acme.com"
                    required
                    className="w-full px-4 py-2.5 bg-dark-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Admin Name</label>
                  <input
                    type="text"
                    value={createForm.admin_name}
                    onChange={e => setCreateForm(prev => ({ ...prev, admin_name: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-4 py-2.5 bg-dark-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {createResult?.error && (
                  <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-2">{createResult.error}</div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowCreateForm(false); setCreateResult(null); }}
                    className="flex-1 py-2.5 bg-dark-700 hover:bg-dark-600 text-gray-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Building2 className="w-12 h-12 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">No organizations found</p>
          <p className="text-gray-600 text-sm mt-1">
            {search ? 'Try adjusting your search' : 'Click "Create Organization" to get started'}
          </p>
        </div>
      ) : (
        <div className="bg-dark-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Plan</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Members</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Created</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(tenant => (
                <tr key={tenant.id} className="border-b border-gray-700/50 hover:bg-dark-800 transition-colors">
                  <td className="px-4 py-3 text-sm text-white font-medium">{tenant.name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-400/10 text-purple-400 capitalize">
                      {tenant.plan || 'standard'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 text-center">{tenant.member_count ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tenant.active !== 0 && tenant.active !== false
                        ? 'bg-green-400/10 text-green-400'
                        : 'bg-gray-400/10 text-gray-400'
                    }`}>
                      {tenant.active !== 0 && tenant.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewMembers(tenant.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 text-orange-400 rounded-md text-xs font-medium hover:bg-orange-500/20 transition-colors"
                        title="View members"
                      >
                        <Users className="w-3.5 h-3.5" />
                        Members
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(tenant.id)}
                        disabled={deletingId === tenant.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-md text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete organization"
                      >
                        {deletingId === tenant.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && tenants.length > 0 && (
        <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-2">{error}</div>
      )}
    </div>
  );
};

export default PlatformOrganizations;
